import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useMetroReport, MetroMetrics } from '@/hooks/useMetroReport';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChevronDown, ChevronRight, MapPin, DollarSign, TrendingUp, Target, Users, Briefcase } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { MetricCard, MetricComparison } from './MetricCard';
import { useComparisonMetrics, ComparisonMetrics } from '@/hooks/useComparisonMetrics';
import { calculateChange } from '@/utils/periodComparison';

interface MetroReportProps {
  startDate: Date;
  endDate: Date;
  timezone?: string;
  compareEnabled?: boolean;
}

export function MetroReport({ startDate, endDate, timezone: propTimezone, compareEnabled = false }: MetroReportProps) {
  const { timezone: contextTimezone } = useTimezoneUtils();
  const timezone = propTimezone || contextTimezone;
  const { formatMoney } = useCompanyLocale();

  const { data: metroData, isLoading } = useMetroReport(startDate, endDate, timezone);
  const [expandedMetros, setExpandedMetros] = useState<Set<string>>(new Set());

  // NOTE: Must be called before any early returns to respect React's rules of hooks
  const { data: comparisonData, isLoading: comparisonLoading } = useComparisonMetrics(
    startDate,
    endDate,
    timezone,
    !isLoading && !!metroData && compareEnabled
  );

  const toggleMetro = (metroId: string) => {
    const newExpanded = new Set(expandedMetros);
    if (newExpanded.has(metroId)) {
      newExpanded.delete(metroId);
    } else {
      newExpanded.add(metroId);
    }
    setExpandedMetros(newExpanded);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const getComparison = (
    current: number,
    previousKey: keyof ComparisonMetrics,
    isPositiveGood = true
  ): MetricComparison | undefined => {
    if (!comparisonData || !compareEnabled) return undefined;
    const change = calculateChange(current, comparisonData.metrics[previousKey]);
    return { ...change, label: comparisonData.period.label, isPositiveGood };
  };

  const totalRevenue = metroData?.reduce((sum, m) => sum + m.total_revenue, 0) || 0;
  const totalJobs = metroData?.reduce((sum, m) => sum + m.total_jobs, 0) || 0;
  const totalCompletedJobs = metroData?.reduce((sum, m) => sum + (m.completed_jobs ?? 0), 0) || 0;
  const totalLeads = metroData?.reduce((sum, m) => sum + m.total_leads, 0) || 0;
  const overallLeadToJob = totalLeads > 0 ? (metroData?.reduce((sum, m) => sum + (m.total_leads > 0 ? (m.lead_to_job_rate ?? 0) * m.total_leads : 0), 0) || 0) / totalLeads : 0;
  const overallJobDone = totalJobs > 0 ? (totalCompletedJobs / totalJobs) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <MetricCard
          title="Total Metros"
          value={metroData?.length || 0}
          icon={MapPin}
        />
        <MetricCard
          title="Total Revenue"
          value={formatMoney(totalRevenue)}
          icon={DollarSign}
          comparison={getComparison(totalRevenue, 'totalTurnover', true)}
          comparisonLoading={compareEnabled && comparisonLoading}
        />

        <MetricCard
          title="Total Leads"
          value={totalLeads}
          icon={Users}
          comparison={getComparison(totalLeads, 'totalLeads', true)}
          comparisonLoading={compareEnabled && comparisonLoading}
        />
        <MetricCard
          title="Total Jobs"
          value={totalJobs}
          icon={Briefcase}
          comparison={getComparison(totalJobs, 'totalJobs', true)}
          comparisonLoading={compareEnabled && comparisonLoading}
        />
        <MetricCard
          title="Lead to Job %"
          value={`${overallLeadToJob.toFixed(1)}%`}
          icon={Target}
        />
        <MetricCard
          title="Job Done %"
          value={`${overallJobDone.toFixed(1)}%`}
          icon={TrendingUp}
        />
      </div>

      {/* Metro Table with Expandable Postcodes */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12"></TableHead>
              <TableHead>Metro</TableHead>
              <TableHead>State</TableHead>
              <TableHead className="text-right">Leads</TableHead>
              <TableHead className="text-right">Jobs</TableHead>
              <TableHead className="text-right">Revenue</TableHead>
              <TableHead className="text-right">Lead to Job %</TableHead>
              <TableHead className="text-right">Job Done %</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {metroData?.map((metro) => (
              <MetroRow
                key={metro.metro_id}
                metro={metro}
                isExpanded={expandedMetros.has(metro.metro_id)}
                onToggle={() => toggleMetro(metro.metro_id)}
              />
            ))}
            {(!metroData || metroData.length === 0) && (
              <TableRow>
              <TableCell colSpan={9} className="text-center text-muted-foreground">
                  No metro data available for this period
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function MetroRow({ metro, isExpanded, onToggle }: { metro: MetroMetrics; isExpanded: boolean; onToggle: () => void }) {
  const { formatMoney } = useCompanyLocale();
  return (
    <>
      <TableRow className="cursor-pointer hover:bg-muted/50" onClick={onToggle}>
        <TableCell>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </TableCell>
        <TableCell className="font-medium">{metro.metro_name}</TableCell>
        <TableCell>{metro.state || '-'}</TableCell>
        <TableCell className="text-right">{metro.total_leads}</TableCell>
        <TableCell className="text-right">{metro.total_jobs}</TableCell>
        <TableCell className="text-right">{formatMoney(metro.total_revenue)}</TableCell>
        <TableCell className="text-right">{(metro.lead_to_job_rate ?? 0).toFixed(1)}%</TableCell>
        <TableCell className="text-right">{(metro.job_done_rate ?? 0).toFixed(1)}%</TableCell>
      </TableRow>
      {isExpanded && metro.postcodes.length > 0 && (
        <TableRow>
          <TableCell colSpan={8} className="bg-muted/20 p-0">
            <div className="p-4">
              <h4 className="text-sm font-semibold mb-2 text-muted-foreground">Postcodes in {metro.metro_name}</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Postcode</TableHead>
                    <TableHead>Suburb</TableHead>
                    <TableHead className="text-right">Leads</TableHead>
                    <TableHead className="text-right">Jobs</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Lead to Job %</TableHead>
                    <TableHead className="text-right">Job Done %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {metro.postcodes.map((postcode) => (
                    <TableRow key={postcode.postcode}>
                      <TableCell className="font-mono">{postcode.postcode}</TableCell>
                      <TableCell>{postcode.suburb || '-'}</TableCell>
                      <TableCell className="text-right">{postcode.totalLeads}</TableCell>
                      <TableCell className="text-right">{postcode.totalJobs}</TableCell>
                      <TableCell className="text-right">{formatMoney(postcode.totalRevenue)}</TableCell>
                      <TableCell className="text-right">{(postcode.leadToJobRate ?? 0).toFixed(1)}%</TableCell>
                      <TableCell className="text-right">{(postcode.jobDoneRate ?? 0).toFixed(1)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TableCell>
        </TableRow>
      )}
      {isExpanded && metro.postcodes.length === 0 && (
        <TableRow>
          <TableCell colSpan={8} className="bg-muted/20 text-center text-sm text-muted-foreground py-4">
            No postcode data available for this metro
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
